import { useCallback, useEffect, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { api, InvestigationListItem } from "./api";
import InvestigationList from "./InvestigationList";
import InvestigationDetail from "./InvestigationDetail";
import InvestigationUpsell from "./InvestigationUpsell";
import CallDetail from "./CallDetail";
import FileDetail from "./FileDetail";
import { UploadProvider, UploadTray } from "./InvestigationUpload";
import { NowPlayingProvider } from "./NowPlayingContext";
import { StaffViewBanner, StaffViewProvider, useStaffAccess } from "./StaffView";

/**
 * Top-level router for /investigations/*. Fetches the list once and passes
 * it down; the upload tray lives here (outside <Routes>) so route changes
 * never interrupt an in-progress batch.
 */
const InvestigationsRouter: React.FC = () => {
  const { addons, refreshAddons } = useAuth();
  const [items, setItems] = useState<InvestigationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const entitled = Boolean(addons.investigations?.entitled);
  // Staff opening a customer's investigation from the staff portal: read-only,
  // and allowed without a seat of their own.
  const { access: staffAccess, checking: staffChecking } = useStaffAccess();

  const fetchList = useCallback(async () => {
    try {
      setItems(await api.list());
    } catch (error: any) {
      if (error?.response?.status !== 403) {
        console.error("Error fetching investigations:", error);
        toast.error("Failed to load investigations");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (entitled) fetchList();
    else setLoading(false);
  }, [entitled, fetchList]);

  // Returning from Stripe checkout: re-check entitlement once.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("addon") === "success") {
      refreshAddons();
    }
  }, [refreshAddons]);

  // Poll while anything is processing so counts and badges flip on their own.
  useEffect(() => {
    if (!items.some((i) => i.processingCount > 0)) return;
    const t = setInterval(fetchList, 8000);
    return () => clearInterval(t);
  }, [items, fetchList]);

  const routes = (
    <Routes>
      <Route index element={<InvestigationList items={items} loading={loading} onRefresh={fetchList} />} />
      <Route path=":investigationId" element={<InvestigationDetail onListChanged={fetchList} />} />
      <Route path=":investigationId/:tab" element={<InvestigationDetail onListChanged={fetchList} />} />
      <Route path=":investigationId/calls/:callId" element={<CallDetail />} />
      <Route path=":investigationId/files/:fileId" element={<FileDetail />} />
    </Routes>
  );

  if (!entitled && staffChecking) return null;
  if (!entitled && !staffAccess) return <InvestigationUpsell />;

  return (
    <StaffViewProvider access={staffAccess}>
    <UploadProvider onBatchComplete={fetchList}>
      <NowPlayingProvider>
      {staffAccess ? (
        // The dashboard lays pages out in a row; stack the banner above.
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          <StaffViewBanner />
          {routes}
        </div>
      ) : (
        routes
      )}
      </NowPlayingProvider>
      <UploadTray />
    </UploadProvider>
    </StaffViewProvider>
  );
};

export default InvestigationsRouter;
